import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

// A refetch for one of the results-view infinite lists (pending or graded).
type Refetcher = () => unknown;

interface ResultsReconcileValue {
  // Register a list's refetch; returns an unregister cleanup.
  register: (fn: Refetcher) => () => void;
  // Refetch every registered list (used after a grade lands).
  reconcile: () => void;
}

const ResultsReconcileContext = createContext<ResultsReconcileValue | null>(null);

/**
 * Scopes the results view so a grade in ONE list reconciles the others too.
 *
 * The member's pending section and the graded results table are separate
 * infinite requests in sibling Suspense boundaries — grading a play from one
 * only refetched that list, so a just-graded play left pending but stayed
 * invisible in the graded table until a full page refresh (#1284). Each list
 * registers its refetch here; {@link useReconcileResults} refetches all of them
 * on a successful grade, so the play moves across live.
 */
export function ResultsReconcileProvider({ children }: { children: React.ReactNode }) {
  const refetchersRef = useRef<Set<Refetcher>>(new Set());

  const register = useCallback((fn: Refetcher) => {
    refetchersRef.current.add(fn);
    return () => {
      refetchersRef.current.delete(fn);
    };
  }, []);

  const reconcile = useCallback(() => {
    for (const fn of refetchersRef.current) {
      try {
        // Best-effort: one list's refetch throwing shouldn't skip the others.
        fn();
      } catch {
        // ignore
      }
    }
  }, []);

  const value = useMemo(() => ({ register, reconcile }), [register, reconcile]);

  return (
    <ResultsReconcileContext.Provider value={value}>{children}</ResultsReconcileContext.Provider>
  );
}

/**
 * Registers an infinite list's refetch so a grade elsewhere in the results view
 * reconciles it too. No-op outside a {@link ResultsReconcileProvider}.
 */
export function useRegisterReconcile(refetch?: Refetcher) {
  const ctx = useContext(ResultsReconcileContext);

  // Keep the latest refetch without re-registering every render.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    if (!ctx) {
      return;
    }
    return ctx.register(() => refetchRef.current?.());
  }, [ctx]);
}

/**
 * Returns a callback that refetches every registered results list, or
 * `undefined` outside a {@link ResultsReconcileProvider}.
 */
export function useReconcileResults() {
  return useContext(ResultsReconcileContext)?.reconcile;
}
